import { ApiError } from "./apiError";

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const LEGACY_EXCEL_MIME = "application/vnd.ms-excel";
const CSV_MIME = "text/csv;charset=utf-8";

export const BINARY_REQUEST_CONFIG = {
  responseType: "arraybuffer",
  transformResponse: [(data) => data],
  headers: {
    Accept: "*/*",
  },
};

function getHeader(headers, name) {
  if (!headers) {
    return "";
  }

  if (typeof headers.get === "function") {
    return headers.get(name) || "";
  }

  return headers[name] || headers[name.toLowerCase()] || "";
}

function decodeText(buffer) {
  return new TextDecoder("utf-8", { fatal: false }).decode(buffer);
}

function toArrayBuffer(data) {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  }

  throw new Error("پاسخ فایل نامعتبر است.");
}

function skipPreamble(bytes) {
  let index = 0;

  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    index = 3;
  }

  while (
    index < bytes.length &&
    (bytes[index] === 0x20 ||
      bytes[index] === 0x09 ||
      bytes[index] === 0x0a ||
      bytes[index] === 0x0d)
  ) {
    index += 1;
  }

  return index;
}

function looksLikeJson(buffer) {
  const bytes = new Uint8Array(buffer);
  const index = skipPreamble(bytes);
  return bytes[index] === 0x7b || bytes[index] === 0x5b;
}

function parseJsonBuffer(buffer) {
  try {
    return JSON.parse(decodeText(buffer));
  } catch {
    return null;
  }
}

function kindFromMime(contentType = "") {
  const mime = String(contentType).toLowerCase();

  if (
    mime.includes("spreadsheetml") ||
    mime.includes("openxmlformats-officedocument.spreadsheetml")
  ) {
    return "xlsx";
  }

  if (mime.includes("text/csv") || mime.includes("application/csv")) {
    return "csv";
  }

  if (
    mime.includes("vnd.ms-excel") ||
    mime.includes("ms-excel") ||
    mime.includes("excel") ||
    mime.includes("text/html") ||
    mime.includes("application/xml") ||
    mime.includes("text/xml")
  ) {
    return "xls";
  }

  return null;
}

function detectSpreadsheetKind(buffer, contentType = "") {
  const bytes = new Uint8Array(buffer);
  const index = skipPreamble(bytes);

  if (bytes[index] === 0x50 && bytes[index + 1] === 0x4b) {
    return "xlsx";
  }

  if (
    bytes[index] === 0xd0 &&
    bytes[index + 1] === 0xcf &&
    bytes[index + 2] === 0x11 &&
    bytes[index + 3] === 0xe0
  ) {
    return "xls";
  }

  const head = decodeText(bytes.subarray(index, index + 200)).trim().toLowerCase();

  if (
    head.startsWith("<html") ||
    head.startsWith("<!doctype") ||
    head.startsWith("<table") ||
    head.startsWith("<meta")
  ) {
    return "html";
  }

  if (
    head.startsWith("<?xml") ||
    head.startsWith("<workbook") ||
    head.includes("urn:schemas-microsoft-com:office:spreadsheet")
  ) {
    return "xls";
  }

  return kindFromMime(contentType);
}

function csvEscape(value) {
  const text = String(value ?? "").replace(/\r\n|\r|\n/g, " ").replace(/"/g, '""');
  return `"${text}"`;
}

function htmlTableToCsv(html) {
  if (typeof DOMParser === "undefined") {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const rows = Array.from(doc.querySelectorAll("tr"));

  if (!rows.length) {
    return null;
  }

  const csv = rows
    .map((row) =>
      Array.from(row.querySelectorAll("th,td"))
        .map((cell) => csvEscape(cell.textContent?.trim() ?? ""))
        .join(",")
    )
    .join("\r\n");

  return csv || null;
}

function convertHtmlSpreadsheet(buffer) {
  const bytes = new Uint8Array(buffer);
  const index = skipPreamble(bytes);
  const html = decodeText(bytes.subarray(index));
  const csv = htmlTableToCsv(html);

  if (!csv) {
    return null;
  }

  // UTF-8 BOM helps Excel open Persian text correctly.
  return new TextEncoder().encode(`\uFEFF${csv}`);
}

function normalizeFilename(filename, kind) {
  const extension = kind === "html" ? "xls" : kind;
  const fallback =
    extension === "xls" ? "export.xls" : extension === "csv" ? "export.csv" : "export.xlsx";

  if (!filename) {
    return fallback;
  }

  const trimmed = String(filename).trim().replace(/^["']|["']$/g, "");
  const withoutExt = trimmed.replace(/\.(xlsx|xls|csv)$/i, "");

  return `${withoutExt}.${extension === "html" ? "xls" : extension}`;
}

function mimeForKind(kind, declaredMime = "") {
  if (kind === "csv") {
    return CSV_MIME;
  }

  if (kind === "xls" || kind === "html") {
    return declaredMime && !declaredMime.includes("json")
      ? declaredMime
      : LEGACY_EXCEL_MIME;
  }

  if (kind === "xlsx") {
    return EXCEL_MIME;
  }

  return declaredMime || "application/octet-stream";
}

export function getFilenameFromDisposition(header) {
  if (!header) {
    return null;
  }

  const utf8Match = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);

  if (utf8Match?.[1]) {
    const raw = utf8Match[1].trim().replace(/^"(.*)"$/s, "$1");

    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  const asciiMatch = /filename=(?:"([^"]+)"|([^;]+))/i.exec(header);

  if (!asciiMatch) {
    return null;
  }

  const raw = (asciiMatch[1] || asciiMatch[2] || "").trim();

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function downloadBlob(data, filename, mimeType = "application/octet-stream") {
  const blob =
    data instanceof Blob
      ? new Blob([data], { type: mimeType || data.type || "application/octet-stream" })
      : new Blob([data], { type: mimeType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function isSpreadsheetDownload(filename, contentType = "") {
  const mime = String(contentType).toLowerCase();

  return (
    /\.(xlsx|xls|csv)$/i.test(filename || "") ||
    mime.includes("excel") ||
    mime.includes("spreadsheet") ||
    mime.includes("text/html") ||
    mime.includes("text/csv") ||
    mime.includes("application/xml") ||
    mime.includes("text/xml")
  );
}

function prepareSpreadsheetDownload(buffer, contentType, contentDisposition, fallbackFilename) {
  let kind = detectSpreadsheetKind(buffer, contentType) || "xlsx";
  let payload = buffer;

  if (kind === "html") {
    const csvBytes = convertHtmlSpreadsheet(buffer);

    if (csvBytes) {
      kind = "csv";
      payload = csvBytes;
    } else {
      kind = "xls";
    }
  }

  const baseName =
    getFilenameFromDisposition(contentDisposition) || fallbackFilename || "download";

  return {
    payload,
    filename: normalizeFilename(baseName, kind),
    mimeType: mimeForKind(kind, contentType),
  };
}

export function saveBinaryDownload(buffer, { contentType = "", contentDisposition = "", fallbackFilename } = {}) {
  if (looksLikeJson(buffer)) {
    const payload = parseJsonBuffer(buffer);
    throw new ApiError(payload?.message || "پاسخ سرور نامعتبر است.", payload);
  }

  const declaredMime = String(contentType).split(";")[0].trim();
  const rawFallback = fallbackFilename || "download";

  if (isSpreadsheetDownload(rawFallback, declaredMime) || detectSpreadsheetKind(buffer, declaredMime)) {
    const prepared = prepareSpreadsheetDownload(
      buffer,
      declaredMime,
      contentDisposition,
      rawFallback
    );
    downloadBlob(prepared.payload, prepared.filename, prepared.mimeType);
    return;
  }

  const filename = getFilenameFromDisposition(contentDisposition) || rawFallback;
  downloadBlob(buffer, filename, declaredMime || "application/octet-stream");
}

export function saveAxiosFile(response, fallbackFilename) {
  saveBinaryDownload(toArrayBuffer(response.data), {
    contentType: getHeader(response.headers, "content-type"),
    contentDisposition: getHeader(response.headers, "content-disposition"),
    fallbackFilename,
  });
}

export function decodeBinaryErrorMessage(data) {
  if (!data) {
    return null;
  }

  if (typeof data.message === "string") {
    return data.message;
  }

  try {
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      return parseJsonBuffer(toArrayBuffer(data))?.message || null;
    }
  } catch {
    return null;
  }

  return null;
}
